import React from 'react'
import { Head } from '@inertiajs/react'
import { AdminLayout } from '@/components/admin/templates/AdminLayout'
import { Stack } from '@/components/admin/atoms/layout/Stack'
import { Inline } from '@/components/admin/atoms/layout/Inline'
import { SplitLayout } from '@/components/admin/atoms/layout/SplitLayout'
import { SplitMain } from '@/components/admin/atoms/layout/SplitMain'
import { SplitSide } from '@/components/admin/atoms/layout/SplitSide'
import { Heading } from '@/components/admin/atoms/typography/Heading'
import { Text } from '@/components/admin/atoms/typography/Text'
import { FormField } from '@/components/admin/molecules/form/FormField'
import { FormSection } from '@/components/admin/molecules/form/FormSection'
import { TextInput } from '@/components/admin/atoms/inputs/TextInput'
import { TextareaInput } from '@/components/admin/atoms/inputs/TextareaInput'
import { SelectInput } from '@/components/admin/atoms/inputs/SelectInput'
import { SearchableSelect } from '@/components/admin/atoms/inputs/SearchableSelect'
import { MultiSearchableSelect } from '@/components/admin/atoms/inputs/MultiSearchableSelect'
import { RichTextEditor } from '@/components/admin/atoms/inputs/RichTextEditor'
import { FileInput } from '@/components/admin/atoms/inputs/FileInput'
import { CheckboxInput } from '@/components/admin/atoms/inputs/CheckboxInput'
import { DateTimeInput } from '@/components/admin/atoms/inputs/DateTimeInput'
import { ActionButton } from '@/components/admin/atoms/actions/ActionButton'
import { AlertBox } from '@/components/admin/atoms/feedback/AlertBox'
import { MediaPickerDialog } from '@/components/admin/organisms/MediaPickerDialog'
import { usePostsEditVM } from './Edit.vm'
import type { PageProps } from '@/types/inertia.d'
import type { AdminPost } from '@/types/admin'

type Props = PageProps<{
  post: AdminPost & {
    tags: { id: number; name: string; slug: string }[]
    category: { id: number; name: string; slug: string } | null
  }
  tags: { id: number; name: string; slug: string }[]
  categories: { id: number; name: string; slug: string }[]
}>

const PostsEdit: React.FC<Props> = ({ post, tags, categories }) => {
  const vm = usePostsEditVM(post)

  return (
    <AdminLayout breadcrumbs={[{ label: 'Posts', href: '/admin/posts' }, { label: 'Edit Post' }]}>
      <Head title={`Edit: ${post.title}`} />
      <Stack gap="6">
        <Inline justify="between" align="center">
          <Heading level={2}>Edit Post</Heading>
          <Inline gap="2">
            <ActionButton variant="ghost" onClick={vm.handleCancel} disabled={vm.saving}>
              Cancel
            </ActionButton>
            <ActionButton variant="outline" onClick={() => vm.handleSubmit('draft')} loading={vm.saving}>
              Save Draft
            </ActionButton>
            <ActionButton variant="primary" onClick={() => vm.handleSubmit('published')} loading={vm.saving}>
              Publish
            </ActionButton>
          </Inline>
        </Inline>

        {vm.errors.base && (
          <AlertBox variant="error">{vm.errors.base}</AlertBox>
        )}

        <SplitLayout ratio="2-1">
          <SplitMain>
            <Stack gap="4">
              <FormSection>
                <FormField label="Title" htmlFor="title" required>
                  <TextInput id="title" value={vm.title} onChange={vm.handleTitleChange} placeholder="Post title…" />
                </FormField>
                <FormField label="Slug" htmlFor="slug">
                  <TextInput id="slug" value={vm.slug} onChange={vm.setSlug} placeholder="post-slug" />
                </FormField>
                <FormField label="Excerpt" htmlFor="excerpt">
                  <TextareaInput id="excerpt" value={vm.excerpt} onChange={vm.setExcerpt} placeholder="Brief summary…" rows={2} />
                </FormField>
              </FormSection>

              <FormSection>
                <Text size="sm" weight="semibold">Content</Text>
                <RichTextEditor value={vm.content} onChange={vm.setContent} />
              </FormSection>

              <FormSection title="SEO">
                <FormField label="Meta Title" htmlFor="meta_title">
                  <TextInput id="meta_title" value={vm.metaTitle} onChange={vm.setMetaTitle} placeholder="SEO title…" />
                </FormField>
                <FormField label="Meta Description" htmlFor="meta_description">
                  <TextareaInput id="meta_description" value={vm.metaDescription} onChange={vm.setMetaDescription} placeholder="SEO description…" rows={2} />
                </FormField>
              </FormSection>
            </Stack>
          </SplitMain>

          <SplitSide>
            <Stack gap="4">
              <FormSection title="Publish" padding="5">
                <FormField label="Status" htmlFor="status">
                  <SelectInput
                    id="status"
                    value={vm.status}
                    onChange={vm.setStatus}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'published', label: 'Published' },
                    ]}
                  />
                </FormField>
                <FormField label="Visibility" htmlFor="visibility">
                  <SelectInput
                    id="visibility"
                    value={vm.visibility}
                    onChange={vm.setVisibility}
                    options={[
                      { value: 'public', label: 'Public' },
                      { value: 'private', label: 'Private' },
                    ]}
                  />
                </FormField>
                <FormField label="Publish Date" htmlFor="published_at">
                  <DateTimeInput id="published_at" value={vm.publishedAt} onChange={vm.setPublishedAt} />
                </FormField>
                <CheckboxInput
                  checked={vm.sticky}
                  onChange={vm.setSticky}
                  label="Sticky post"
                  size="sm"
                />
              </FormSection>

              <FormSection title="Category" padding="5">
                <SearchableSelect
                  value={vm.categoryId}
                  onChange={vm.setCategoryId}
                  placeholder="Select category…"
                  options={categories.map((c) => ({ value: c.id, label: c.name }))}
                />
              </FormSection>

              <FormSection title="Tags" padding="5">
                <MultiSearchableSelect
                  selectedValues={vm.selectedTagIds}
                  onChange={vm.handleTagsChange}
                  placeholder="Select tags…"
                  options={tags.map((t) => ({ value: t.id, label: t.name }))}
                />
              </FormSection>

              <FormSection title="Featured Image" padding="5">
                <Stack gap="2">
                  <FileInput onChange={vm.handleCoverChange} preview={vm.coverPreview} accept="image/*" />
                  <Inline gap="2">
                    <ActionButton
                      variant="outline"
                      size="xs"
                      onClick={() => vm.setMediaDialogOpen(true)}
                    >
                      Choose from Media
                    </ActionButton>
                    {vm.coverPreview && (
                      <ActionButton
                        variant="ghost"
                        size="xs"
                        onClick={vm.removeCover}
                      >
                        Remove image
                      </ActionButton>
                    )}
                  </Inline>
                </Stack>
              </FormSection>
            </Stack>
          </SplitSide>
        </SplitLayout>
      </Stack>

      <MediaPickerDialog
        open={vm.mediaDialogOpen}
        onSelect={vm.handleMediaSelect}
        onClose={() => vm.setMediaDialogOpen(false)}
      />
    </AdminLayout>
  )
}

export default PostsEdit
